/**
 * Rfis — list page for AISC §1.7.0 Phase 2.
 */

import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, Breadcrumbs, Link as MuiLink,
} from "@mui/material";
import { HomeRounded, AddRounded, WarningAmberRounded, QuestionAnswerOutlined } from "@mui/icons-material";

import { listRfis, RFI_STATUSES } from "../../utils/rfisApi";
import EmptyState from "../../components/EmptyState/EmptyState";
import { fmtDateISO } from "../../utils/dateFmt";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLOR = {
  DRAFT:     { bg: "#e9ecef", color: "#6c757d" },
  SENT:      { bg: "#cfe2ff", color: "#052c65" },
  ANSWERED:  { bg: "#d1e7dd", color: "#0a3622" },
  CLOSED:    { bg: "#198754", color: "#ffffff" },
  CANCELLED: { bg: "#f8d7da", color: "#58151c" },
};

export default function Rfis() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const jobIdParam = params.get("job_id") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setNotAvailable(false);
    const filters = {};
    if (jobIdParam) filters.job_id = jobIdParam;
    if (statusFilter && statusFilter !== "ALL") filters.status = statusFilter;
    listRfis(filters)
      .then((data) => { if (!cancelled) setRows((data && data.rows) || []); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404 || status === 501) setNotAvailable(true);
        else setError(err?.response?.data?.error || err?.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobIdParam, statusFilter]);

  return (
    <>
      <Helmet><title>RFIs | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}><HomeRounded fontSize="small" /> <span>Home</span></Stack>
          </MuiLink>
          <Typography color="text.primary">RFIs</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Requests for Information</Typography>
          <Button variant="contained" startIcon={<AddRounded />}
            onClick={() => navigate(jobIdParam ? `/rfis/new?job_id=${jobIdParam}` : "/rfis/new")}>
            New RFI
          </Button>
        </Stack>
        <PageGuide
          why="Get a written answer when a drawing or spec is unclear — so no one guesses on the floor."
          who="Anyone on the floor who hits an unclear drawing; the PM and engineer answering."
          what="Your log of questions to the engineer of record, customer, or GC, with their answers."
          how="Open a new RFI, send it, then record the answer when it comes back. Closed RFIs stay on file."
          when="Any time a drawing, dimension, or spec is unclear — before the affected work is fabricated."
          standard="AISC §1.7.0"
        />

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField select label="Status" size="small" sx={{ minWidth: 200 }}
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="ALL">All statuses</MenuItem>
            {RFI_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Paper>

        {notAvailable && <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 2 }}>
          This feature isn't available yet — please contact your administrator.
        </Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          <SmartTable tableId="rfis" lockedColumns={["RFI #", "Status"]}>
            <TableHead><TableRow>
              <TableCell>RFI #</TableCell><TableCell>Job</TableCell><TableCell>Requested of</TableCell>
              <TableCell>Question</TableCell><TableCell>Status</TableCell><TableCell>Created</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "#6c757d" }}>Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && !notAvailable && !error && (
                <TableRow><TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                  <EmptyState
                    icon={QuestionAnswerOutlined}
                    title="No RFIs yet"
                    description="Click New RFI to capture a Request for Information from the customer or EOR. Each RFI is tracked through its lifecycle and surfaces on the Job Activity Log."
                    variant="plain"
                  />
                </TableCell></TableRow>
              )}
              {!loading && rows.map((r) => {
                const c = STATUS_COLOR[r.status] || { bg: "#e9ecef", color: "#495057" };
                return (
                  <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/rfis/${r.id}`)}>
                    <TableCell><strong>{r.rfi_number}</strong></TableCell>
                    <TableCell>Job #{r.job_id}</TableCell>
                    <TableCell>{r.requested_of_party}</TableCell>
                    <TableCell sx={{ maxWidth: 350 }}>
                      <Typography variant="body2" noWrap>{r.information_requested}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={prettyStatus(r.status)}
                      sx={{ backgroundColor: c.bg, color: c.color, fontWeight: 600, fontSize: "0.7rem" }} /></TableCell>
                    <TableCell>{fmtDateISO(r.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Container>
    </>
  );
}
